import React, { ReactNode, useMemo } from "react";

import classNames from "classnames";

import {
  StyledWrapper,
  StyledChildrenWrapper,
  StyledBackgroundCircle,
  StyledForegroundCircle,
  StyledSvg,
  StyledProgressRingPercentage,
} from "./Styles";

export interface ProgressCircleProps {
  /** Progress prop can be number */
  progress?: number;
  /** Background color */
  backgroundColor?: string;
  /** Progress color */
  progressColor?: string;
  /** Radius size */
  radius?: number;
  /** Stroke size */
  stroke?: number;
  /** Should stroke line be rounded */
  roundStroke?: boolean;
  /** Applies passed classes */
  className?: string;
  /** Should the percentage be displayed inside ring or not */
  showPercentage?: boolean;
  /** Size of displayed percentage */
  fontSize?: number;
}

export interface ProgressRingProps extends ProgressCircleProps {
  children?: ReactNode;
}

export const ProgressCircle = ({
  backgroundColor,
  progressColor,
  progress = 0,
  radius = 20,
  stroke = 3,
  roundStroke = false,
  className,
  showPercentage = false,
  fontSize,
}: ProgressCircleProps) => {
  if (stroke > radius) {
    console.warn("Stroke can not have value greater than the radius.");
    stroke = radius;
  }
  const progressNumber = useMemo(() => {
    let width = progress;
    if (progress > 100) {
      width = 100;
    }
    if (progress < 0) {
      width = 0;
    }
    return width;
  }, [progress]);

  const normalizedRadius = useMemo(() => radius - stroke / 2, [radius, stroke]);
  const circumference = useMemo(
    () => normalizedRadius * 2 * Math.PI,
    [normalizedRadius]
  );
  const strokeDashoffsetOuter = useMemo(
    () => circumference - ((100 - progressNumber) / 100) * circumference,
    [circumference, progressNumber]
  );
  const strokeDashoffsetInner = useMemo(
    () => circumference - (progressNumber / 100) * circumference,
    [circumference, progressNumber]
  );
  return (
    <StyledSvg
      height={radius * 2}
      width={radius * 2}
      className={className}
      data-progress={progressNumber}
      data-testid="progress-circle"
    >
      <StyledBackgroundCircle
        $color={backgroundColor}
        $strokeDashOffset={-strokeDashoffsetOuter}
        strokeWidth={stroke}
        strokeDasharray={`${circumference} ${circumference}`}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      <StyledForegroundCircle
        $color={progressColor}
        $strokeDashOffset={strokeDashoffsetInner}
        strokeWidth={stroke}
        strokeDasharray={`${circumference} ${circumference}`}
        $roundStroke={roundStroke}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      {showPercentage ? (
        <StyledProgressRingPercentage
          $color={progressColor}
          $fontSize={fontSize}
          x="50%"
          y="-50%"
          dominantBaseline="central"
          textAnchor="middle"
        >
          {progressNumber}%
        </StyledProgressRingPercentage>
      ) : null}
    </StyledSvg>
  );
};

export const ProgressRing = ({
  className,
  children,
  ...rest
}: ProgressRingProps) => {
  return (
    <StyledWrapper
      className={classNames(className)}
      data-testid="progress-ring"
    >
      <ProgressCircle {...rest} />
      {children ? (
        <StyledChildrenWrapper>{children}</StyledChildrenWrapper>
      ) : null}
    </StyledWrapper>
  );
};

ProgressRing.displayName = "ProgressRing";
